/**
 * Eguchi Homes icon set: a curated, hand-drawn set inline in the bundle, so the
 * app makes no icon requests and works offline from first paint.
 *
 * DESIGN SPEC (every glyph in lib/icons/* follows it)
 * - Grid: 24×24 viewBox. Live area is 2..22 (2px padding all round); only
 *   strokes' round caps may touch the edge.
 * - Stroke: 1.75px, currentColor, round caps and round joins. No hairlines,
 *   no mixed weights. Callers may thicken (strokeWidth) for tiny badges.
 * - Corners: 2.5px radius on device bodies (≥12px wide), 1.5–2px on panels
 *   and screens, 1px on small parts. Circles for dials, knobs, lenses.
 * - Optical sizing: round shapes run ~1px larger than square ones (a 18px
 *   circle sits with a 16px square); tall objects (phone, door, speaker) are
 *   centred on x=12 and use the full height; wide ones (AC, battery, strip) are
 *   centred on y=12.
 * - Detail budget: reads at 16px. Dots are zero-length round-capped strokes,
 *   never tiny circles.
 * - Fill is used ONLY for "active" accents: a soft 28% fill for the lit or open
 *   part (shade glowing, doorway open, daylight past a blind, element heating)
 *   and a solid fill for small indicators (battery charge, thermostat flame,
 *   update dot). Inactive states are pure line drawings.
 * - Levels (Wi-Fi, signal) draw unreached steps at 35% so the full shape is
 *   always legible.
 * - Motion: `spin` parts rotate about the glyph centre (fan running); disabled
 *   under prefers-reduced-motion.
 *
 * DATA: glyphs are SVG path strings in lib/icons/{devices,readings,places,ui}.ts
 * (one file per category), resolved by name. Entity/room/device resolution
 * lives in lib/icons/resolve.ts (React-free, so scripts can run it).
 *
 * Admin overrides use the "eh:<name>" namespace wherever HA would hold an
 * "mdi:" name (entity icon, room icon, device icon).
 */
import { memo, type ReactNode, type SVGProps } from 'react';
import { GLYPHS } from './icons/registry.js';
import type { GlyphDef } from './icons/geom.js';
import './icons/icons.css';

export { hasIcon, GLYPHS, ICON_CATEGORIES, ALIASES } from './icons/registry.js';
export {
  iconFor,
  pickBase,
  withState,
  mdiToIcon,
  roomIcon,
  deviceTypeIcon,
  deviceIcon,
  ICON_NS,
  FALLBACK_ICON,
  type IconSource,
  type IconPick,
} from './icons/resolve.js';
export {
  LIGHT_ICONS,
  LIGHT_ICON_LABEL,
  LIGHT_GROUPS,
  isLightIcon,
  lightFromName,
  lightGroupOf,
  type LightIcon,
  type LightGroupId,
} from './icons/lights.js';
export type { Glyph, GlyphDef } from './icons/geom.js';

export type IconName = string;

export const Icon = memo(function Icon({
  name,
  size = 20,
  strokeWidth = 1.75,
  ...rest
}: { name: string; size?: number; strokeWidth?: number } & SVGProps<SVGSVGElement>): ReactNode {
  const g = GLYPHS[name] ?? GLYPHS['generic']!;
  const def: GlyphDef = typeof g === 'string' ? { d: g } : g;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {def.g && <path d={def.g} fill="currentColor" fillOpacity={0.28} stroke="none" />}
      {def.f && <path d={def.f} fill="currentColor" stroke="none" />}
      {def.x && <path d={def.x} strokeOpacity={0.35} />}
      <path d={def.d} />
      {def.spin && (
        <g className="eh-spin" style={{ transformOrigin: def.o ?? '12px 12px' }}>
          <path d={def.spin} />
        </g>
      )}
    </svg>
  );
});
